import { create } from 'zustand';
import produce from 'immer';
import dayjs from 'dayjs';

import {
  fetchUserDetails,
  fetchDefaultEmailNotificationSettings,
  fetchAccountInfo,
  fetchLimittedAcessLookup,
  fetchLimittedAccessLookupRecords,
  fetchAdminSettings,
  fetchLoggedInExternalUserDtPermissions,
} from './apiStore';
import {
  fetchEmailNotificationSettings,
  fetchEmailNotificationData,
} from 'src/hooks/useEmailNotificationStore';

import {
  hasAllValuesPresent,
  validateEmail,
  validateRequest,
} from '../utils/userHelper';
import {
  dailyAtSettingsId,
  timerPickerDefaultState,
} from 'src/utils/customEmailNotification';
import { formatStore, isUserDataEqual } from '../utils/datacomparisonHelper';

import {
  UserStore,
  GetUserDetailsType,
  EmailSelectType,
  PermissionsType,
  AccountSettings,
  LimitedAccessRecords,
  OptionType,
} from '../types/userStoreTypes';
import { LimitedAccessLookupType } from '../types/limitedAccessRecords';
import { AccountInfo } from 'src/types/AccountInfo.d';
import { CustomEmailNotificationSettingsType } from 'src/types/emailNotification';
import { LimitedAccessLookup } from '../types/limitedAccessRecords';
import { useSaveChangesParamStore } from 'src/store/useSaveChangesParamStore';
import { MultiSelectInputType } from 'src/ui/components';

import { LimitedAccessLookupTypeRevamp } from '../api/useGetLimitedAccessRecord';

export const useUserStore = create<UserStore>((set, get) => ({
  isExternalUser: false,
  isLoadingExternalUserPermission: false,
  prevInvalidEmail: '',
  loggedUserDTPermission: [],
  showValidationErr: false,
  isResponseLoading: false,
  isAllEmailNotificationLoading: false, // all notification api response loading
  basicUserDetails: {
    firstName: '',
    lastName: '',
    email: '',
    status: '',
    id: '',
  },
  currentData: {},
  initialUserData: {},

  allowReInvite: false,

  accountsInfo: [],

  userMode: '',

  accountIds: [],

  userSettingsPermissions: [],

  personAlertLimiterValues: [],

  optionsByAccount: {},
  initialOptionsByAccount: {},

  includeEmptyNotification: false,
  customEmailNotificationSettings: [],
  defaultEmailNotification: undefined, //  email notification object initially from api and then as per user selection
  emailNotificationSettings: undefined, //all email notification list as required object
  defaultNotificationId: undefined, // default email notification id coming from api

  accountList: [],
  accountQuestionSettings: [],
  isLookupRecordFetching: false,
  selectedRecordId: [],
  selectedChoiceId: [],
  selectedOptions: [],
  selectedChoiceOptions: [],
  isLookupLoading: false,
  isLookupOptionLoading: false,
  isSaveClicked: false,
  isOnboardingCheckLoading: false,
  listPageParam: 0,

  setListPageParam: (val: number) => {
    set(() => ({
      listPageParam: val,
    }));
  },
  isViewPersonAlertsChecked: false,

  setPrevInvalidMailId: (val: string) => {
    set(state => ({
      prevInvalidEmail: val,
    }));
  },

  updateBasicUserDetails: (key: string, value: string) => {
    set(state => ({
      basicUserDetails: { ...state.basicUserDetails, [key]: value },
    }));
  },

  setShowValidationErr: (val: boolean) =>
    set(() => ({
      showValidationErr: val,
    })),

  updateAccountInfo: (value: Array<number>) => {
    set(state => ({
      accountsInfo: [...value],
    }));
  },

  setOptionsByAccount: (
    updater:
      | Record<number, Array<MultiSelectInputType>>
      | ((
          prev: Record<number, Array<MultiSelectInputType>>,
        ) => Record<number, Array<MultiSelectInputType>>),
  ) =>
    set(state => {
      const next =
        typeof updater === 'function'
          ? (updater as Function)(state.optionsByAccount)
          : updater;
      return {
        optionsByAccount: { ...(state.optionsByAccount || {}), ...next },
      };
    }),

  getUserDetails: async ({
    id,
    userMode,
    token,
    tokenType,
    isExternalUser,
    userId,
  }: Partial<GetUserDetailsType>) => {
    // if external user, getting dt access of questions.

    if (
      useSaveChangesParamStore.getState().isSettingsPage === true ||
      useSaveChangesParamStore.getState().isDirty === false
    ) {
      if (isExternalUser) {
        set({
          isLoadingExternalUserPermission: true,
        });
        const loggedUserDtPermissions =
          await fetchLoggedInExternalUserDtPermissions({
            id: userId,
            token,
            tokenType,
          });
        const exteranalDtPermission = loggedUserDtPermissions?.Data?.map(x => {
          return {
            AccountId: x.AccountId,
            AccessTypeId: x.AccessTypeID,
            isDisableFullAccess: x.AccessTypeID === 1,
            Questions:
              x.AccessTypeID === 1 &&
              Array.isArray(x.Questions) &&
              x.Questions?.length > 0
                ? x.Questions.map(question => {
                    return {
                      Id: question.Id,
                      Records: question?.CustomLookupRecords?.filter(
                        (option: any) => option.HasAccess === true,
                      ),
                      IsVisible: question?.CustomLookupRecords?.some(
                        (option: any) => option.HasAccess === true,
                      ),
                    };
                  })
                : [],
            ChoiceQuestions:
              x.AccessTypeID === 1 &&
              Array.isArray(x.ChoiceQuestions) &&
              x.ChoiceQuestions?.length > 0
                ? x.ChoiceQuestions.map(chQn => {
                    return {
                      Id: chQn.Id,
                      Records: chQn.QuestionOptions.filter(
                        option => option.HasAccess === true,
                      ),
                      IsVisible: chQn.QuestionOptions.some(
                        option => option.HasAccess === true,
                      ),
                    };
                  })
                : [],
          };
        });
        set(() => ({
          isExternalUser: true,
          loggedUserDTPermission: [...exteranalDtPermission],
          isLoadingExternalUserPermission: false,
        }));
      }

      if (
        get().userMode !== '' &&
        useSaveChangesParamStore.getState().allowFetch === false
      ) {
        set(
          produce(state => {
            state.isResponseLoading = true;
          }),
        );

        const isCopyMode = get().userMode === 'Copy';

        const response = (
          await fetchUserDetails({
            id: id,
            token: token,
            tokenType: tokenType,
          })
        )?.Data;

        set(
          produce(state => {
            state.basicUserDetails.firstName = isCopyMode
              ? ''
              : response?.FirstName;
            state.basicUserDetails.lastName = isCopyMode
              ? ''
              : response?.LastName;
            state.basicUserDetails.email = isCopyMode ? '' : response?.Email;
            state.basicUserDetails.status = response?.Status;
            state.basicUserDetails.id = isCopyMode ? null : response?.Id;
            state.accountsInfo = response?.AccountIds && [
              ...response?.AccountIds,
            ];
            state.userSettingsPermissions =
              isCopyMode && isExternalUser
                ? response?.Permissions?.filter(
                    (p: any) => p?.ClaimType !== 'CanAdministerUsers',
                  )
                : response?.Permissions;
            state.personAlertLimiterValues = response?.PersonAlertLimiterValues;
            state.allowReInvite = response?.AllowReInvite;

            // to get the already selected PersonAlertLimiterValues
            if (
              response &&
              response.PersonAlertLimiterValues &&
              response.PersonAlertLimiterValues.length > 0
            ) {
              const optionsMap: Record<number, Array<any>> = {};
              response.PersonAlertLimiterValues.forEach((item: any) => {
                const accountId = item.AccountId;
                if (!optionsMap[accountId]) {
                  optionsMap[accountId] = [];
                }
                optionsMap[accountId].push({
                  label: item?.Label,
                  value: item?.AccountCategoryLimitingValueId,
                  userAccountId: item?.UserAccountId,
                });
              });
              state.initialOptionsByAccount = optionsMap;
              state.optionsByAccount = optionsMap;
            } else {
              state.initialOptionsByAccount = {};
            }
          }),
        );
      }

      set(
        produce(state => {
          state.isAllEmailNotificationLoading = true;
        }),
      );

      //get all email notification lists - live, custom, no email
      const allEMailNotifications = (
        await fetchEmailNotificationSettings({
          token: token,
          tokenType: tokenType,
        })
      )?.Data;

      set(
        produce(state => {
          state.isAllEmailNotificationLoading = false;
          state.emailNotificationSettings = allEMailNotifications?.map(item => {
            return {
              id: item?.Id,
              label: item?.NotifiedAt,
              value: item?.NotificationTimeInterval,
            };
          });
        }),
      );

      // to get default email notification - selected email notification
      if (get().userMode !== 'Edit' && get().userMode !== 'Copy') {
        set(
          produce(state => {
            state.isAllEmailNotificationLoading = true;
          }),
        );

        const defaultNotification = await fetchDefaultEmailNotificationSettings(
          {
            token: token,
            tokenType: tokenType,
          },
        );

        set(
          produce(state => {
            state.defaultNotificationId = defaultNotification.Data.Value;
            const allEmailSettings = get().emailNotificationSettings;
            if (allEmailSettings !== undefined) {
              state.defaultEmailNotification = allEmailSettings?.find(
                item => item?.id == defaultNotification.Data.Value,
              );
            }
          }),
        );

        const adminSettings = (
          await fetchAdminSettings({
            token: token,
            tokenType: tokenType,
          })
        )?.Data;

        set(
          produce(state => {
            state.isAllEmailNotificationLoading = false;
            const defaultNotification = get().defaultEmailNotification;
            if (
              adminSettings &&
              defaultNotification !== undefined &&
              defaultNotification.id === dailyAtSettingsId
            ) {
              let eachCustomValue = adminSettings?.NotifyTime.split(',');

              let newCustomValues = eachCustomValue?.map(value => {
                return {
                  ...timerPickerDefaultState,
                  NotifyTime:
                    value !== '' && value !== null
                      ? dayjs(value).format('HH:mm')
                      : timerPickerDefaultState.NotifyTime,
                  EpochTimeValue:
                    value !== null && value !== ''
                      ? dayjs(value).unix()
                      : timerPickerDefaultState.EpochTimeValue,
                };
              });
              if (newCustomValues?.length !== 0)
                state.customEmailNotificationSettings = newCustomValues;
              else
                state.customEmailNotificationSettings = timerPickerDefaultState;
            }
          }),
        );

        const store = useUserStore.getState();
        set({
          initialUserData: formatStore(store),
        });
      }

      //on edit fetch email notifications of a particular user
      if (get().userMode === 'Edit' || get().userMode === 'Copy') {
        set(
          produce(state => {
            state.isAllEmailNotificationLoading = true;
          }),
        );

        const response = (
          await fetchEmailNotificationData({
            token: token,
            tokenType: tokenType,
            id: id,
          })
        )?.Data;

        set(
          produce(state => {
            state.isAllEmailNotificationLoading = false;
            state.includeEmptyNotification = response?.IncludeEmptyNotification;
            if (response?.CustomEmailNotificationSettings?.length !== 0) {
              state.customEmailNotificationSettings =
                response?.CustomEmailNotificationSettings?.map(item => {
                  return {
                    NotifyTime: dayjs(item?.NotifyDateTime).format('HH:mm'),
                    NotifyDateTime: item?.NotifyDateTime,
                    EpochTimeValue: item?.EpochTimeValue,
                    Id: item?.Id,
                    UserEmailNotificationId: item?.UserEmailNotificationId,
                  };
                });
            } else {
              if (
                response?.EmailNotificationSettingsId ===
                get().defaultEmailNotification?.id
              )
                state.customEmailNotificationSettings = [
                  timerPickerDefaultState,
                ];
            }

            let newEmailNotification = get().emailNotificationSettings?.find(
              item => item?.id === response?.EmailNotificationSettingsId,
            );
            if (newEmailNotification === undefined) {
              state.defaultEmailNotification =
                get().emailNotificationSettings?.find(item => item?.id === 1);
            } else {
              state.defaultEmailNotification = newEmailNotification;
            }
          }),
        );
        get().fetchAccountInfo(token, tokenType, id);
      }

      if (get().userMode === '') {
        get().fetchAccountInfo(token, tokenType);
      }

      setTimeout(() => {
        set({ isResponseLoading: false });
      }, 1500);
    }
  },

  deleteUserDetails: () => {
    set(state => {
      return {
        ...state,
        basicUserDetails: {
          firstName: '',
          lastName: '',
          email: '',
          status: '',
          id: '',
        },
        accountsInfo: [],
        accountQuestionSettings: [],
        userSettingsPermissions: [],
        isSaveClicked: false,
        allowReInvite: false,
        customEmailNotificationSettings: [],
        includeEmptyNotification: false,
        selectedRecordId: [],
        selectedChoiceId: [],
        showValidationErr: false,
        prevInvalidEmail: '',
        selectedOptions: [],
        initialUserData: {},
        accountIds: [],
        selectedChoiceOptions: [],
        optionsByAccount: {},
      };
    });
    useSaveChangesParamStore.getState().setIsDirty(false);
  },

  updateUserMode: (value: string) => {
    set(
      produce(state => {
        state.userMode = value;
      }),
    );
  },
  updateInitialData: () => {
    const store = useUserStore.getState();
    set({
      initialUserData: formatStore(store),
    });
  },

  updateAccountQuestionSettings: (val: any) => {
    set({
      accountQuestionSettings: val,
    });
  },

  updatePermissions: (value: Array<PermissionsType>) => {
    set(
      produce(state => {
        state.userSettingsPermissions = value;
      }),
    );
  },

  updateDefaultNotification: (value: EmailSelectType) => {
    set(
      produce(state => {
        state.defaultEmailNotification = value;
      }),
    );
  },

  updateIncludeEmptyNotification: (value: boolean) => {
    set(
      produce(state => {
        state.includeEmptyNotification = value;
      }),
    );
  },

  updateCustomEmailNotificationSettings: (
    value: Array<CustomEmailNotificationSettingsType>,
  ) => {
    if (value) {
      set(
        produce(state => {
          state.customEmailNotificationSettings = [...value];
        }),
      );
    }
  },

  fetchAccountInfo: async (
    token: string | undefined,
    tokenType: string | undefined,
    id: string | undefined,
  ) => {
    fetchAccountInfo({
      token,
      tokenType,
      id,
    }).then((account: any) => {
      if (account) {
        set(
          produce(state => {
            state.accountList = [...account?.Data];
          }),
        );
        if (get().userMode === 'Edit' || get().userMode === 'Copy') {
          const selectedAccounts = get().accountsInfo;
          const accountInfo: Array<AccountInfo> = get().accountList.filter(x =>
            selectedAccounts.includes(x.AccountId),
          );

          console.log('Account Info:', accountInfo);
          //to apply default selection in the record that already selected when edit a user
          //for limited access lookup questions
          const options: Array<string> | undefined | any = accountInfo
            .flatMap(({ Questions }) => Questions)
            .flatMap(({ CustomLookupRecords }) => CustomLookupRecords)
            .filter(x => x.HasAccess === true)
            .map(({ _id }) => _id);
          //for limitedaccess choice questions
          const choiceOptions = accountInfo
            ?.flatMap(({ ChoiceQuestions }) => ChoiceQuestions ?? [])
            ?.flatMap(({ QuestionOptions }) => QuestionOptions ?? [])
            .filter(x => x.HasAccess === true)
            .map(({ Id }) => Id);

          //for limited access records
          const selectedRecords = accountInfo
            .flatMap(({ Questions }) => Questions)
            .flatMap(({ CustomLookupRecords }) => CustomLookupRecords)
            .filter(x => x.HasAccess === true);

          //all limited access lookup questions
          const selectedQuestions = accountInfo.flatMap(
            ({ Questions }) => Questions,
          );

          const selectedChoiceRecords = accountInfo
            .flatMap(({ ChoiceQuestions }) => ChoiceQuestions ?? [])
            .flatMap(({ QuestionOptions }) => QuestionOptions ?? [])
            .filter(x => x.HasAccess === true);

          const selectedChoiceQuestions = accountInfo.flatMap(
            ({ ChoiceQuestions }) => ChoiceQuestions,
          );

          //for already existing limited access lookup
          const existingRecordOption = selectedRecords?.map(item => {
            return {
              AccountId:
                accountInfo?.find(info =>
                  info?.Questions?.find(question =>
                    question?.CustomLookupRecords?.find(
                      (record: any) =>
                        record?.CustomLookupId === item?.CustomLookupId,
                    ),
                  ),
                )?.AccountId || -1,
              OptionId: item?._id,
              QuestionId:
                selectedQuestions?.find(qn =>
                  qn?.CustomLookupRecords?.find(
                    (record: any) =>
                      record?.CustomLookupId === item?.CustomLookupId,
                  ),
                )?.Id || -1,
            };
          });

          //for already existing limited access choice
          const existingChoiceRecordOption = selectedChoiceRecords?.map(
            item => {
              return {
                AccountId:
                  accountInfo?.find(info =>
                    info?.ChoiceQuestions?.find(question =>
                      question?.QuestionOptions?.find(
                        record => record?.Id === item?.Id,
                      ),
                    ),
                  )?.AccountId || -1,
                OptionId: item?.Id,
                QuestionId:
                  selectedChoiceQuestions?.find(qn =>
                    qn?.QuestionOptions?.find(
                      record => record?.Id === item?.Id,
                    ),
                  )?.Id || -1,
              };
            },
          );

          if (options.length > 0 || existingRecordOption?.length > 0) {
            get().setSelectedRecords(options);
            get().setSelectedOptions(existingRecordOption);
          }
          if (
            choiceOptions?.length > 0 ||
            existingChoiceRecordOption?.length > 0
          ) {
            get().setSelectedChoices(choiceOptions);
            get().setSelectedChoiceOptions(existingChoiceRecordOption);
          }
          get().accountsInfo.forEach(id => {
            get().updateAccountSettings(id, true);
          });
        }
        const store = useUserStore.getState();
        set({
          initialUserData: formatStore(store),
        });
      }
    });
  },

  updateAccountSettings: (id: number, isUpdate: boolean) => {
    if (isUpdate) {
      const isAccountPresent =
        get().accountQuestionSettings &&
        get().accountQuestionSettings?.some(obj =>
          Object.values(obj).includes(id),
        );

      if (!isAccountPresent) {
        const accountInfo: AccountInfo | undefined = getAccountDetails(
          get().accountList,
          id,
        );
        if (accountInfo) {
          set(
            produce(state => {
              state.accountQuestionSettings = [
                ...state.accountQuestionSettings,
                {
                  AccountName: accountInfo.Name,
                  AccountId: accountInfo.AccountId,
                  CID: accountInfo.CID,
                  SID: accountInfo.SID,
                  PID: accountInfo.PID,
                  AccessTypeId:
                    get().isExternalUser && get().userMode == ''
                      ? get().loggedUserDTPermission.find(
                          acc => acc.AccountId === accountInfo.AccountId,
                        ).AccessTypeId
                      : accountInfo.AccessTypeID,
                  HasLimitedAccessLookups: accountInfo.HasLimitedAccessLookups,
                  Questions: accountInfo.Questions?.map(item => {
                    return {
                      ...item,
                      QuestionType: 'LookupQuestionTypeOptions',
                    };
                  }),
                  ChoiceQuestions: accountInfo.ChoiceQuestions?.map(item => {
                    return {
                      ...item,
                      QuestionType: 'ChoiceQuestionTypeOptions',
                    };
                  }),
                  AccountCategoryLimitingValues: [
                    ...accountInfo.AccountCategoryLimitingValues,
                  ],
                  LimitCategory: accountInfo.LimitCategory,
                },
              ];
            }),
          );
        }
      } else {
        let accountQuestionSettings = get().accountQuestionSettings;

        const filteredArray = accountQuestionSettings?.filter(obj =>
          get().accountIds.includes(obj.AccountId),
        );
        set(
          produce(state => {
            state.accountQuestionSettings = filteredArray;
          }),
        );
      }
    } else {
      const settings: Array<AccountSettings> | undefined =
        get().accountQuestionSettings;

      set(
        produce(state => {
          state.accountQuestionSettings = settings?.filter(
            x => x.AccountId !== id,
          );
        }),
      );
    }
  },
  //update selected account information obj during select-all
  bulckUpdateAccountSettings: (accountIds: Array<number> | [] | undefined) => {
    set(
      produce(state => {
        state.accountIds = accountIds;
      }),
    );
    if (accountIds && accountIds.length) {
      accountIds.forEach(id => {
        get().updateAccountSettings(id, true);
      });
    } else {
      set(
        produce(state => {
          state.accountQuestionSettings = [];
        }),
      );
    }
  },

  updateDtAccessSettings: (key, value, id) => {
    set(
      produce(state => {
        state.accountQuestionSettings = get().accountQuestionSettings?.map(
          x => {
            if (x.AccountId === id) {
              return { ...x, [key]: value };
            }
            return x;
          },
        );
      }),
    );
  },

  fetchLimittedAccessLookup: async (
    token: string,
    tokenType: string,
    id: number,
    userId: string | undefined,
  ) => {
    set({
      isLookupLoading: true,
    });

    await fetchLimittedAcessLookup({
      token,
      tokenType,
      id,
      userId,
    }).then((lookup: LimitedAccessLookupTypeRevamp | undefined) => {
      if (lookup?.Data) {
        const accountSettings = get().accountQuestionSettings;

        //for limited accesss lookups
        const records = lookup.Data?.Questions?.filter(
          (item: any) => item.AccountId === id,
        );

        // for choice type questions
        const choicerecords = lookup.Data?.ChoiceQuestions;

        const LookupQuestions = records.map((item: any) => ({
          TableName: item.TableName,
          Columns: item.Columns,
          Id: item.CustomLookupTableId,
          QuestionType: 'LookupQuestionTypeOptions',
          QuestionOptions: item.CustomLookupRecords,
        }));

        const ChoiceQuestions = choicerecords.map((item: any) => ({
          FieldLabel: item.FieldLabel,
          // Columns: item.Columns,
          Id: item.Id,
          QuestionType: 'ChoiceQuestionTypeOptions',
          QuestionOptions: item.QuestionOptions,
        }));

        let accountSettingsInfo = accountSettings?.find(
          settings => settings.AccountId === id,
        );

        if (accountSettingsInfo) {
          accountSettingsInfo = {
            ...accountSettingsInfo,
            Questions: LookupQuestions,
            ChoiceQuestions: ChoiceQuestions,
          };
        }

        set(
          produce(state => {
            state.accountQuestionSettings = accountSettings?.map(obj =>
              obj.AccountId === id ? { ...obj, ...accountSettingsInfo } : obj,
            );
            state.isLookupLoading = false;
          }),
        );
      } else {
        set({ isLookupLoading: false });
      }
    });
  },

  fetchLookupRecords: async (
    token: string,
    tokenType: string,
    accountId: number,
    recordId: number,
  ) => {
    set({
      isLookupOptionLoading: true,
    });
    await fetchLimittedAccessLookupRecords({
      token,
      tokenType,
      accountId,
      recordId,
    }).then((records: LimitedAccessLookup | undefined) => {
      if (records) {
        const accountSettings = get().accountQuestionSettings;
        const { Records } = records;
        set(
          produce(state => {
            state.accountQuestionSettings = accountSettings?.map(account => {
              if (account.AccountId === accountId) {
                const toUpdate: Array<LimitedAccessRecords> | undefined =
                  account.Questions?.map(lookup =>
                    parseInt(lookup.Id) === recordId &&
                    lookup?.QuestionType === 'LookupQuestionTypeOptions'
                      ? { ...lookup, Records: [...Records] }
                      : lookup,
                  );

                const choiceQnUpdate = account.ChoiceQuestions?.map(lookup =>
                  lookup.Id === recordId &&
                  lookup?.QuestionType === 'ChoiceQuestionTypeOptions'
                    ? { ...lookup, Records: [...Records] }
                    : lookup,
                );

                return {
                  ...account,
                  Questions: [...(toUpdate ?? [])],
                  ChoiceQuestions: [...(choiceQnUpdate ?? [])],
                };
              }
              return account;
            });
            state.isLookupOptionLoading = false;
          }),
        );
      }
    });
  },

  setIsLookupRecordFetching: (value: boolean) => {
    set(
      produce(state => {
        state.isLookupRecordFetching = value;
      }),
    );
  },
  setSaveClicked: (value: boolean) => {
    set(
      produce(state => {
        state.isSaveClicked = value;
      }),
    );
  },

  setViewPersonAlertsChecked: (value: boolean) => {
    set(
      produce(state => {
        state.isViewPersonAlertsChecked = value;
      }),
    );
  },

  setSelectedRecords: (ids: Array<string>) => {
    if (ids?.length && ids?.length > 0) {
      set(
        produce(state => {
          state.selectedRecordId = [...ids];
        }),
      );
    } else {
      set(
        produce(state => {
          state.selectedRecordId = [];
        }),
      );
    }
  },

  setSelectedChoices: (ids: Array<string>) => {
    if (ids?.length && ids?.length > 0) {
      set(
        produce(state => {
          state.selectedChoiceId = [...ids];
        }),
      );
    } else {
      set(
        produce(state => {
          state.selectedChoiceId = [];
        }),
      );
    }
  },

  setSelectedOptions: (options: Array<OptionType>) => {
    if (Array.isArray(options) && options?.length > 0) {
      set(
        produce(state => {
          state.selectedOptions = [...options];
        }),
      );
    } else {
      set(
        produce(state => {
          state.selectedOptions = [];
        }),
      );
    }
  },

  setSelectedChoiceOptions: (options: Array<any>) => {
    set(
      produce(state => {
        state.selectedChoiceOptions = [...options];
      }),
    );
  },

  setIsOnboardingCheckLoading: (value: boolean) => {
    set(
      produce(state => {
        state.isOnboardingCheckLoading = value;
      }),
    );
  },

  //to clear the selected limiter options for new user
  clearOptionsByAccount: () => {
    set(
      produce(state => {
        state.optionsByAccount = {};
      }),
    );
  },

  updateOptionsByAccount: (value: any) =>
    set(() => ({
      optionsByAccount: value,
    })),

  enableSettings: (type: string): any => {
    switch (type) {
      case 'basic-tab':
        return (
          hasAllValuesPresent(get().basicUserDetails) &&
          validateEmail(get().basicUserDetails.email)
        );
      case 'account-tab':
        return get().accountsInfo.length > 0;

      case 'permission-tab':
        return !validateRequest(
          get().selectedOptions,
          get().accountQuestionSettings,
          get().userSettingsPermissions,
          get().selectedChoiceOptions,
          get().optionsByAccount,
        );

      default:
        return false;
    }
  },
  checkDataHasChanged: () => {
    const store = useUserStore.getState();
    set({
      currentData: formatStore(store),
    });

    if (Object.keys(get().initialUserData).length === 0) return true;

    if (isUserDataEqual(get().initialUserData, get().currentData)) {
      useSaveChangesParamStore.getState().setIsDirty(false);
      return true;
    } else if (Object.keys(get().initialUserData).length > 0) {
      useSaveChangesParamStore.getState().setIsDirty(true);
      return false;
    } else if (
      get().userMode !== 'Edit' &&
      Object.keys(get().initialUserData).length === 0
    ) {
      useSaveChangesParamStore.getState().setIsDirty(false);
      const store = useUserStore.getState();
      set({
        initialUserData: formatStore(store),
      });
      return false;
    } else return false;
  },
}));

function getAccountDetails(accountList: Array<AccountInfo>, AccountId: number) {
  return accountList.find(x => x.AccountId === AccountId);
}
